import { useRef, useState } from 'react'
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import clsx from 'clsx'
import { ITooltipProps } from './types'
import styles from './styles.module.scss'

const Tooltip = ({
  className = '',
  color = 'blue',
  content,
  maxWidth = 200,
  position = 'top',
  size = 'md',
  style,
  trigger,
  ...props
}: ITooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const arrowHeight = 6
  const arrowWidth = 10
  const gap = 4

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      arrow({
        element: arrowRef,
      }),
      flip(),
      offset(arrowHeight + gap),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
    placement: position,
    transform: false,
  })

  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, {
    role: 'tooltip',
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  return (
    <>
      <span ref={refs.setReference} className={styles.trigger} {...getReferenceProps()}>
        {trigger}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            className={clsx('mui-tooltip', styles.tooltip, styles[size], styles[color], className)}
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              maxWidth: `${maxWidth}px`,
              ...style,
            }}
            {...props}
            {...getFloatingProps()}
          >
            {content}
            <FloatingArrow
              className={styles.arrow}
              context={context}
              height={arrowHeight}
              ref={arrowRef}
              tipRadius={2}
              width={arrowWidth}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

export default Tooltip
